/**
 * i18n configuration using react-i18next.
 *
 * Loads translations from src/locales/{lang}.json files.
 * English is bundled eagerly (synchronous default); other languages are
 * lazy-loaded as separate chunks via import.meta.glob.
 * Language preference is stored in localStorage under STORAGE_KEYS.LANGUAGE.
 *
 * Usage in components:
 *   import { useTranslation } from "react-i18next";
 *   const { t } = useTranslation();
 *   t("AUTH.GENERAL.SIGNUP_BUTTON")
 *
 * Must be imported once in root layout or a provider to initialize.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { STORAGE_KEYS } from "@/config/constants";
import enTranslation from "../../locales/en.json";

/**
 * Lazy loaders for every non-English locale, one chunk per file.
 * Vite resolves this glob at build time against src/locales/*.json.
 */
const localeLoaders = import.meta.glob<Record<string, unknown>>(
  ["../../locales/*.json", "!../../locales/en.json"],
  { import: "default" },
);

const TRANSLATION_KEY_PATTERN = /^[A-Z][A-Z0-9_]*(\.[A-Za-z0-9_]+)+$/;
const warnedMissingKeys = new Set<string>();

/** Text shown for a key found in no locale file, English included: the call's default, else the key's last segment as words; warns once per key in dev. */
function missingKeyText(key: string, defaultValue?: string): string {
  if (import.meta.env.DEV && !warnedMissingKeys.has(key)) {
    warnedMissingKeys.add(key);
    console.warn(`[i18n] Missing translation key in en.json: ${key}`);
  }
  if (defaultValue !== undefined) return defaultValue;
  // Plain text passed straight to t() is already readable, so only key-shaped strings are rewritten.
  if (!TRANSLATION_KEY_PATTERN.test(key)) return key;
  const words = key.slice(key.lastIndexOf(".") + 1).replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function getStoredLanguage(): string {
  try {
    return (
      (typeof window !== "undefined" &&
        localStorage.getItem(STORAGE_KEYS.LANGUAGE)) ||
      "en"
    );
  } catch {
    return "en";
  }
}

function getInitialLanguage(): string {
  return typeof window === "undefined" ? "en" : getStoredLanguage();
}

/**
 * Lazily loads a translation JSON chunk from src/locales/.
 * Falls back to the bundled English translation if the file isn't found.
 */
async function loadTranslation(lang: string): Promise<Record<string, unknown>> {
  if (lang === "en") return enTranslation as Record<string, unknown>;
  const loader = localeLoaders[`../../locales/${lang}.json`];
  if (loader) {
    try {
      const resources = await loader();
      // A chunk can resolve without a default export, and i18next then runs
      // JSON.parse(JSON.stringify(undefined)) and throws out of addResourceBundle.
      if (resources && typeof resources === "object") return resources;
    } catch {
      // fall through to English fallback
    }
  }
  return enTranslation as Record<string, unknown>;
}

const initialLanguage = getInitialLanguage();

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    lng: initialLanguage,
    fallbackLng: "en",
    returnEmptyString: false,
    parseMissingKeyHandler: missingKeyText,
    interpolation: { escapeValue: false },
    resources: {
      en: { translation: enTranslation },
      ...(initialLanguage === "en"
        ? {}
        : { [initialLanguage]: { translation: {} } }),
    },
    react: {
      useSuspense: false,
    },
  });
}

/** Initialize i18n. Call this once on app boot. */
export async function initI18n() {
  const lang = getStoredLanguage();
  const resources = await loadTranslation(lang);
  i18n.addResourceBundle(lang, "translation", resources, true, true);
  await i18n.changeLanguage(lang);

  return i18n;
}

/** Change language at runtime — loads new translations and persists preference. */
export async function changeLanguage(lang: string) {
  // Load translations if not already loaded
  if (!i18n.hasResourceBundle(lang, "translation")) {
    const resources = await loadTranslation(lang);
    i18n.addResourceBundle(lang, "translation", resources);
  }

  await i18n.changeLanguage(lang);

  try {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
  } catch {
    // Silent fail in restricted environments
  }
}

export default i18n;
